import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  LabelList,
  ResponsiveContainer,
} from 'recharts';
import { Typography, Box, Divider } from '@mui/material';
import { FlexBox } from 'components/Containers/FlexBox';
import { Circle } from '@mui/icons-material';
import useAppTheme from 'hooks/useAppTheme';
import { useMemo, useCallback, memo, useState } from 'react';
import { useAppSelector } from 'store/hook';
import {
  formatNumberToShortScale,
  getSortedCategoryWiseWorkOrderData,
} from '../utils/dataFormatters';
import { CustomLoadingButton } from 'components/Buttons/CustomLoadingButton/CustomLoadingButton';
import { useNavigate } from 'react-router-dom';
import { Selector } from 'components/Form/Selectors/Selector';
import { filterWorkOrders } from 'utils/workorders';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const MAX_CHAR = 12;

const CustomYAxisTick = memo(({ x, y, payload, fontColor }: any) => {
  const [line1Raw, line2Raw] = (payload?.value || '').split('-');
  const line1 = line1Raw?.trim() || '';
  const line2 = line2Raw?.trim() || '';
  const isSame = line1.toLowerCase() === line2.toLowerCase();

  const truncate = (text: string) => {
    if (!text) return '';
    const capped = text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
    return capped.length > MAX_CHAR
      ? capped.slice(0, MAX_CHAR - 3) + '...'
      : capped;
  };

  const displayLine1 = truncate(line1);
  const displayLine2 = isSame ? '' : truncate(line2);

  return (
    <text
      x={x}
      y={y}
      dy={4}
      textAnchor="end"
      fill={fontColor || '#666'}
      fontSize={11}
    >
      <tspan x={x} dy={0}>
        {displayLine1}
      </tspan>
      {displayLine2 && (
        <tspan
          x={x}
          dy={12}
          textAnchor="left"
          style={{
            textTransform: 'capitalize',
          }}
        >
          {displayLine2}
        </tspan>
      )}
      <title>{line2 ? `${line1} - ${line2}` : line1}</title>
    </text>
  );
});

CustomYAxisTick.displayName = 'CustomYAxisTick';

const CustomLegend = memo(
  ({
    legendItems,
  }: {
    legendItems: Array<{
      label: string;
      color: string;
    }>;
  }) => {
    const { enableDarkTheme } = useAppTheme();
    const palette = enableDarkTheme
      ? clientThemePalette[CLIENT_THEME].dark
      : clientThemePalette[CLIENT_THEME].light;

    return (
      <Box display="flex" gap={1}>
        {legendItems.map((item) => (
          <Box key={item.label} display="flex" alignItems="center" gap="2px">
            <Circle sx={{ fontSize: 14, color: item.color }} />
            <Typography
              sx={{
                fontSize: 12,
                fontWeight: 500,
                color: palette.textPrimary,
              }}
            >
              {item.label}
            </Typography>
          </Box>
        ))}
      </Box>
    );
  }
);

CustomLegend.displayName = 'CustomLegend';

const formatCategoryText = (text: string) => {
  if (!text) return '';
  return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
};

export const FrequentlyReplacedParts = () => {
  const { t } = useTranslation();
  const chartView = [
    { display: t('frequently_replaced'), id: 'frequently_replaced' },
    { display: t('maintenance_frequency'), id: 'maintained_parts' },
  ];
  const { enableDarkTheme } = useAppTheme();
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const navigate = useNavigate();
  const selectedRangeWorkOrderData = useAppSelector(
    (state) =>
      state.squaresPartsMaintenanceExpenditureReducer?.data?.currentData
  );

  const [filterOption, setFilterOption] = useState('frequently_replaced');

  // Process category stats
  const categoryData = useMemo(() => {
    const filteredWorkOrders = filterWorkOrders(selectedRangeWorkOrderData);

    return getSortedCategoryWiseWorkOrderData(filteredWorkOrders, {
      onlyReplace: filterOption === 'frequently_replaced',
    });
  }, [selectedRangeWorkOrderData, filterOption]);

  // Process data for chart
  const processedData = useMemo(() => {
    return categoryData.map((item) => {
      const [component, subcomponent] = item.category.split('_');
      const formattedComponent = formatCategoryText(component || '');
      const formattedSubcomponent = formatCategoryText(subcomponent || '');

      return {
        ...item,
        component: formattedComponent,
        subcomponent: formattedSubcomponent,
        label: `${formattedComponent}\n${formattedSubcomponent}`,
        cost: item?.cost > 0 ? item.cost : 0,
        count: item?.count > 0 ? item.count : 0,
        partCost: item?.total_part_cost > 0 ? item.total_part_cost : 0,
        labourCost: item?.total_labour_cost > 0 ? item.total_labour_cost : 0,
        totalStackedCost:
          (item?.total_part_cost || 0) + (item?.total_labour_cost || 0),
      };
    });
  }, [categoryData]);

  // Memoize tooltip formatter
  const tooltipFormatter = useCallback((value: any, name: string) => {
    return [
      name?.toLowerCase().includes('cost')
        ? `$${Number(value).toLocaleString()}`
        : value,
      name,
    ];
  }, []);

  const handleViewAllClick = () => {
    const tabIndex = filterOption === 'frequently_replaced' ? 2 : 1;
    navigate(`/x-parts/insights-and-recommendations?tab=${tabIndex}`);
  };

  const barConfig = useMemo(() => {
    if (filterOption === 'frequently_replaced') {
      return [
        {
          key: 'count',
          label: t('count'),
          fill: palette.primary,
          stacked: false,
          showLabel: true,
        },
        {
          key: 'cost',
          label: t('cost'),
          fill: '#58BF6A',
          stacked: false,
          showLabel: true,
        },
      ];
    }
    return [
      {
        key: 'count',
        label: t('count'),
        fill: palette.primary,
        stacked: false,
        showLabel: true,
      },
      {
        key: 'partCost',
        label: t('part_cost'),
        fill: '#58BF6A',
        stacked: 'costStack',
        showLabel: false,
      },
      {
        key: 'labourCost',
        label: t('labour_cost'),
        fill: '#FF9800',
        stacked: 'costStack',
        showLabel: false,
      },
      {
        key: 'totalStackedCost',
        label: t('total_cost'),
        fill: '#58BF6A',
        stacked: false,
        showLabel: true,
        isOverlay: true,
      },
    ];
  }, [filterOption]);

  const legendItems = useMemo(() => {
    const temp = barConfig
      .filter((el) => el.key !== 'totalStackedCost')
      .map((el) => ({
        label: el.label,
        color: el.fill,
      }));
    return temp;
  }, [barConfig]);

  return (
    <Box
      sx={{
        backgroundColor: enableDarkTheme
          ? palette.toolBarBackground
          : palette.toolBarBackground,
        borderRadius: '5px',
        border: enableDarkTheme ? '1px solid #2F445D' : '1px solid #BDBDBD',
      }}
    >
      <Box display={'flex'} flexDirection={'column'} p={1}>
        <Box display={'flex'} flexDirection={'column'} gap={1} width={'100%'}>
          <Box
            display={'flex'}
            flexDirection={'row'}
            gap={4}
            justifyContent={'space-between'}
          >
            <Typography
              sx={{ color: enableDarkTheme ? '#fff' : '#000', fontSize: 14 }}
            >
              {t('parts')}
            </Typography>
            <Selector
              value={filterOption}
              selectorOptions={chartView}
              onChange={(e) => {
                setFilterOption(e.target.value as string);
              }}
              enableDarkTheme={enableDarkTheme}
              minWidth="150px"
            />
          </Box>
          <CustomLegend legendItems={legendItems} />
        </Box>

        <FlexBox
          sx={{
            justifyContent: 'center',
            alignItems: 'center',
            width: '100%',
            height: '450px',
          }}
        >
          {processedData.length === 0 ? (
            <Typography sx={{ color: enableDarkTheme ? '#F5DEB3' : '#000' }}>
              {t('no_data_2')}
            </Typography>
          ) : (
            <Box width="100%" height={'450px'} overflow="scroll">
              <ResponsiveContainer
                width="100%"
                height={
                  processedData.length < 6 ? 6 * 60 : processedData.length * 60
                }
              >
                <BarChart
                  data={processedData}
                  layout="vertical"
                  margin={{ top: 0, right: 40, left: 0, bottom: 0 }}
                  barCategoryGap={10}
                  barGap={2}
                >
                  <CartesianGrid
                    strokeDasharray="3 3"
                    horizontal
                    vertical={false}
                    stroke={palette.cartesianGrid}
                  />

                  <XAxis
                    type="number"
                    dataKey="cost"
                    orientation="top"
                    xAxisId="cost"
                    hide={true}
                  />
                  <XAxis
                    type="number"
                    dataKey="count"
                    orientation="bottom"
                    xAxisId="count"
                    hide={true}
                  />

                  <YAxis
                    type="category"
                    dataKey="label"
                    tick={<CustomYAxisTick fontColor={palette.grey} />}
                    width={80}
                  />

                  <Tooltip
                    contentStyle={{
                      backgroundColor: enableDarkTheme
                        ? palette.boxBackground
                        : palette.boxBackground,
                      border: `1px solid ${
                        enableDarkTheme ? palette.grey : palette.lightGrey
                      }`,
                      borderRadius: 4,
                      padding: '6px 10px',
                      boxShadow: 'none',
                    }}
                    labelStyle={{
                      color: enableDarkTheme ? '#fff' : '#000',
                      fontSize: 12,
                      fontWeight: 500,
                      marginBottom: 4,
                    }}
                    itemStyle={{
                      color: enableDarkTheme ? '#fff' : '#000',
                      fontSize: 12,
                      padding: '2px 0',
                    }}
                    formatter={tooltipFormatter}
                    cursor={{
                      fill: enableDarkTheme ? '#1B2B4A' : '#EEEEEE',
                      opacity: 0.3,
                    }}
                  />

                  {barConfig.map((bar, idx) => {
                    // attach overlay label to the *last stacked bar* (labourCost)
                    if (bar.isOverlay) return null;

                    return (
                      <Bar
                        key={bar.key}
                        dataKey={bar.key}
                        name={bar.label}
                        fill={bar.fill}
                        barSize={15}
                        xAxisId={bar.key.includes('count') ? 'count' : 'cost'}
                        stackId={
                          typeof bar.stacked === 'string'
                            ? bar.stacked
                            : undefined
                        }
                        radius={
                          bar.key === 'partCost' ? [0, 0, 0, 0] : [0, 4, 4, 0]
                        }
                      >
                        {bar.showLabel && (
                          <LabelList
                            dataKey={bar.key}
                            position="right"
                            formatter={(v: any) =>
                              bar.key.includes('cost')
                                ? `$${formatNumberToShortScale(v)}`
                                : v
                            }
                            style={{
                              fontSize: 11,
                              fill: enableDarkTheme ? '#fff' : '#000',
                            }}
                          />
                        )}

                        {filterOption !== 'frequently_replaced' &&
                          bar.key === 'labourCost' && (
                            <LabelList
                              dataKey="totalStackedCost"
                              position="right"
                              formatter={(v: any) =>
                                `$${formatNumberToShortScale(v)}`
                              }
                              style={{
                                fontSize: 11,
                                fill: enableDarkTheme ? '#fff' : '#000',
                              }}
                            />
                          )}
                      </Bar>
                    );
                  })}
                </BarChart>
              </ResponsiveContainer>
            </Box>
          )}
        </FlexBox>
      </Box>

      <Divider
        sx={{
          background: enableDarkTheme ? '#2F445D' : '#9E9E9E',
        }}
      />
      <Box display={'flex'} alignItems={'center'} justifyContent={'center'}>
        <CustomLoadingButton variant="text" onClick={handleViewAllClick}>
          {t('view_all')}
        </CustomLoadingButton>
      </Box>
    </Box>
  );
};
